import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { Avatar, Button, styled } from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { v4 as uuidV4 } from 'uuid';
import PopoverTooltip from '../PopoverTooltip';
import colorClasses from './utils/colorClasses';
import getHashColorClass from './utils/getHashColorClass';
import letters from './utils/letters';

const ColoredAvatar = styled(Avatar)({
  ...colorClasses,
  fontWeight: 700,
  fontSize: 16,
});

type AvatarIndicatorProps =
  | {
    user?: undefined;
    tooltip?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    tooltipText?: string;
  }
  | {
    user: null;
    tooltip?: boolean;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    tooltipText?: string;
  }
  | {
    user: { displayName: string; };
    tooltip?: false;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    tooltipText?: null;
  }
  | {
    user: { displayName: string; };
    tooltip: true;
    tooltipText?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  };

const StyledButton = styled(Button)({
  padding: 0,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  borderRadius: '50%',
});

export default function AvatarIndicator(props: AvatarIndicatorProps) {
  const {
    user, tooltip, tooltipText, onClick,
  } = props;

  const avatarContent = React.useMemo(() => {
    if (user === undefined) {
      return <AccountCircleIcon />;
    }
    if (user === null) {
      return <img src="assets/cogNeedIcon.svg" alt="CogNeed" style={{ width: 28, height: 28 }} />;
    }
    return letters(user.displayName);
  }, [user]);

  const avatarElement = React.useMemo(() => {
    const colorClass = typeof (avatarContent) === 'string' ? getHashColorClass(avatarContent) : undefined;
    return (
      <ColoredAvatar className={clsx('default', {
        ...(colorClass && { [colorClass]: true }),
        system: user === null,
        transparent: user === undefined,
      })}
      >
        {avatarContent}
      </ColoredAvatar>
    );
  }, [user, avatarContent]);

  const tooltipValue = React.useMemo(() => {
    if (user) return user.displayName;
    if (tooltipText) return tooltipText;
    return undefined;
  }, [user, tooltipText]);

  const wrappedWithTooltip = React.useMemo(() => {
    if (!tooltip || !tooltipValue) {
      return avatarElement;
    }
    return (
      <PopoverTooltip
        popoverId={`avatar-${uuidV4()}`}
        text={avatarElement}
      >
        {tooltipValue}
      </PopoverTooltip>
    );
  }, [avatarElement, tooltip, tooltipValue]);

  const wrappedWithButton = React.useMemo(() => {
    if (onClick) {
      return <StyledButton onClick={onClick}>{wrappedWithTooltip}</StyledButton>;
    }
    return wrappedWithTooltip;
  }, [onClick, wrappedWithTooltip]);

  return wrappedWithButton;
}
